/*************************************************************************
 *
 * KLEARNOW CONFIDENTIAL
 * __________________
 *
 *  Copyright (c) 2018 - 2020 KLEARNOW Corporation.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of KLEARNOW Corporation and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to KLEARNOW Corporation
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from KLEARNOW Corporation.
 */

import React, { Component } from 'react'
import { Input, Form, Icon, Popup } from 'semantic-ui-react'
import SemanticDatepicker from 'react-semantic-ui-datepickers'
import 'react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css'
import {propsAreEqual_NoRerenderNeeded} from '../reducer/util'
import { triggerFormValidationSoon } from '../validation/triggerFormValidation'

const normalizeDisplayValue = (value) =>
  value === '_BLANK_' || value == '-999' ? '' : value;

export class InputField extends Component {
  constructor(props) {
    super(props);
    this.state = {
      wasFocused: false,
      wasChanged: false,
      clearTextSpinner: false,
      // Seed so Return to Edit remount does not replay a consumed rubber-band.
      newValue: props.newValue,
      rbCounterID: props.rbCounterID,
    }
  }
  getRubberBindFieldKey = () => this.props.id + this.props.shipmentId;

  isRubberBindForField = (props) =>
    props.fieldName === this.getRubberBindFieldKey();

  componentWillReceiveProps(nextProps) {
    const rubberBind = this.isRubberBindForField(nextProps);
    const valueChanged = nextProps.newValue !== this.state.newValue;
    const counterChanged = nextProps.rbCounterID !== this.state.rbCounterID;

    if (!valueChanged && !counterChanged) return;

    // Not this field — sync tracking only (avoid setState / re-render on sibling binds)
    if (!rubberBind) {
      this.state.newValue = nextProps.newValue;
      this.state.rbCounterID = nextProps.rbCounterID;
      return;
    }

    // Same click (or remount with the already-consumed counter) — do not
    // overwrite later edits with the stale rubber-banded FormValues.newValue.
    if (!counterChanged) {
      this.state.newValue = nextProps.newValue;
      return;
    }

    const nextLocalState = {
      newValue: nextProps.newValue,
      rbCounterID: nextProps.rbCounterID,
    };

    if (nextProps.newValue != null) {
      nextLocalState.focusValueX = normalizeDisplayValue(nextProps.newValue);
      nextLocalState.isFocused = true;
      nextLocalState.wasChanged = true;
    }

    this.setState(nextLocalState, () => {
      const rawValue = nextProps.newValue;
      const valueToApply = this.props.isInteger
        ? parseFloat(String(rawValue).replace(/[^\d.-]/g, '')).toFixed(2)
        : rawValue;
      // Rubber-bind has no DOM event — drive onChange + onBlur (PGA/line save lives on blur)
      this.props.onChange && this.props.onChange(valueToApply);
      this.props.onBlur && this.props.onBlur(valueToApply);
    });
  }

  shouldComponentUpdate(nextProps, nextState){
    if(window.slowstate)return true;
    if (nextProps.errors !== this.props.errors) return true;
    if (nextProps.warnings !== this.props.warnings) return true;
    if ((nextState.isFocused || this.state.isFocused) && nextProps.persistOnBlurOnly) {
      // Allow Redux/prop value updates while focused (rubber-bind persist must not be blocked)
      if (nextProps.value !== this.props.value) return true;
      if (nextProps.rbCounterID !== this.props.rbCounterID) return true;
      if (
        this.isRubberBindForField(nextProps) &&
        nextProps.newValue !== this.props.newValue
      ) {
        return true;
      }
      if (nextState.focusValueX !== this.state.focusValueX) return true;
      if (nextState.isFocused !== this.state.isFocused) return true;
      if (nextState.wasChanged !== this.state.wasChanged) return true;
      if (nextProps.disabled !== this.props.disabled) return true;
      if (nextState.clearTextSpinner !== this.state.clearTextSpinner) return true;
      return false;
    }
    return !propsAreEqual_NoRerenderNeeded(this.props,nextProps) || !propsAreEqual_NoRerenderNeeded(this.state,nextState);
  }
  
  errorPopup = errors => {
    const error = errors.map((info, index) => {
      return (
        <div className='error' key={index}>
          * {info}
        </div>
      )
    })
    return (
      <Popup trigger={<Icon name='warning sign' color='red' />} content={error || ''} />
    )
  }

  warningPopup = warnings => {
    const warning = warnings.map((info, index) => {
      return (
        <div className='error' key={index}>
          * {info}
        </div>
      )
    })
    return (
      <Popup trigger={<Icon name='warning sign' color='yellow' />} content={warning || ''} />
    )
  }

  emptyField = id => {
    this.setState({clearTextSpinner:true})
    setTimeout(()=>{
      !this.props.isInteger && this.props.onChange('')
      let element = document.getElementById(id)
      element && element.focus()
      this.setState({clearTextSpinner:false})
    },1000)
  }
  render() {
    const {
      id,
      dateClass,
      subLabelText,
      subLabelToFixed,
      iconPosition,
      label,
      width,
      isLabel,
      className,
      type,
      onChange,
      icon,
      subLabelClass,
      onBlur,
      disabled,
      value,
      labelClassName,
      setINPUTID,
      penClicked,
      isInteger,
      shipmentId,
      handleFocus,
      labelIcon,
      clearInput,
      maxlength,
      errors,
      warnings,
      postLabel,
      onKeyUp,
      filterDate,
      minDate,
      maxDate,
      showToday,
      saveOnTabOut,
      persistOnBlurOnly,
      onLiveChange,
      formatWhileTyping,
    } = this.props
    const {
      clearTextSpinner,
      isFocused,
      wasChanged,
      focusValueX,
    } = this.state;
    let redBgColor = "";
    let yellowBgColor = "";
    if(warnings && warnings.length){
      yellowBgColor = "yellowBgColor";
    }
    if(errors && errors.length){
      redBgColor = "redBgColor";
    }

    const normalizedPropValue = normalizeDisplayValue(value);
    const displayValue =
      persistOnBlurOnly && isFocused && wasChanged && focusValueX != null
        ? normalizeDisplayValue(focusValueX)
        : normalizedPropValue;
    // Coerce so numeric id + missing shipmentId never yields NaN for htmlFor/id
    const fieldDomId = `${id ?? ''}${shipmentId ?? ''}`;
    
    return (
      <React.Fragment>
        {type !== 'date' && (
          <label
            htmlFor={fieldDomId}
            style={{cursor:disabled?'not-allowed':'text'}}
            className={
              labelClassName
                ? 'name-label es ' + labelClassName
                : 'name-label es'
            }
          >
            {(errors && errors.length) ? this.errorPopup(errors) : ''}
            {(warnings && warnings.length) ? this.warningPopup(warnings) : ''}
            {label}
            {labelIcon && labelIcon}
          </label>
        )}
        <Form.Field
          onClick={()=>{ this.dateUserTouched = true; this.setState({wasFocused:true}); }}
          onMouseDown={()=>{ this.dateUserTouched = true; this.setState({wasFocused:true}); }}
          className={dateClass + ' date-field '+'subclass-'+subLabelClass+' '+redBgColor+yellowBgColor}
          width={width}
        >
          {isLabel && <label size='small'> {label} </label>}
          {type === 'date' && labelIcon}
          {type === 'date' ? (
            <React.Fragment key={`date-${id}-${(errors && errors.length) || 0}`}>
            <SemanticDatepicker
              name={id}
              id={fieldDomId}
              clearable={true}
              onDateChange={value => {
                const nextMs = value?.getTime?.() ?? null;
                const prevMs = this.state.lastSaveHS?.getTime?.() ?? null;
                const isClear = value == null;
                const propHasDate =
                  this.props.value !== -999 &&
                  this.props.value > 0 &&
                  this.props.value !== undefined;

                // Deduplicate identical date picks. Do not treat null===null as a
                // no-op when props still have a date — the library often primes
                // lastSaveHS to null on mount while selected still shows the value,
                // which previously blocked clear from calling onChange/API.
                if (!isClear && nextMs === prevMs) return;
                if (isClear && prevMs == null && !propHasDate) return;

                // Skip library mount noise before the user has touched this control
                if (!this.dateUserTouched && !this.state.wasFocused) {
                  this.setState({ lastSaveHS: value });
                  return;
                }

                this.dateUserTouched = true;
                this.setState({ lastSaveHS: value, wasFocused: true });
                if (!filterDate || filterDate(value)) {
                  onChange && onChange(isClear ? null : value, isClear ? null : value);
                } else {
                  onChange && onChange(null, null);
                }
                triggerFormValidationSoon();
              }}
              selected={(value !== -999 && value > 0 && value !== undefined) ? new Date(new Date(value)) : false}
              onBlur={event => {
                onBlur && onBlur(new Date(event.target.value));
                triggerFormValidationSoon();
              }}
              onFocus={()=>{ this.dateUserTouched = true; this.setState({wasFocused:true}); }}
              pointing={'left'}
              filterDate={filterDate}
              showToday={this.props.hasOwnProperty("showToday") === true ? showToday : true}
              minDate={minDate}
              maxDate={maxDate}
              className={className+" "+redBgColor+ yellowBgColor}
              label={<label className='floating-date-label'> {(errors && errors.length) ? this.errorPopup(errors) : ''} {label}</label>}
              icon='calendar outline'
              format='MM/DD/YYYY'
              disabled={disabled}
              clearOnSameDateClick={false}
            />
          </React.Fragment>) : (
          
              <Input
              className={redBgColor || yellowBgColor}
                value={displayValue}
                width={width}
                iconPosition={iconPosition}
                id={fieldDomId}
                name={fieldDomId}
                type={type}
                title={displayValue}
                placeholder={this.props.placeholder}
                onChange={event => {
                  let nextValue = event.target.value;
                  if (formatWhileTyping) {
                    nextValue = formatWhileTyping(nextValue);
                  }
                  if (persistOnBlurOnly) {
                    onLiveChange && onLiveChange(nextValue, event);
                    this.setState({
                      focusValueX: nextValue,
                      isFocused: true,
                      wasChanged: true,
                    });
                    return;
                  }
                  onChange && onChange(nextValue, event);
                  this.setState({
                    focusValueX: nextValue,
                    isFocused: true,
                    wasChanged: true,
                    currentValueRerenderer: Date.now(),
                  });
                }}
                onBlur={event => {
                  let nextValue = event.target.value;
                  if (formatWhileTyping) {
                    nextValue = formatWhileTyping(nextValue);
                  }
                  this.setState({ isFocused: false, focusValueX: nextValue });
                   if(!this.state.wasChanged && !saveOnTabOut && value == this.state.focusValueX)return;
                  if (persistOnBlurOnly) {
                    if (this.state.wasChanged || saveOnTabOut || value != nextValue) {
                      if (isInteger && !nextValue) {
                        onChange && onChange('0.00', event);
                      } else {
                        onChange && onChange(nextValue, event);
                      }
                      triggerFormValidationSoon()
                    }
                    onBlur && onBlur(nextValue, event);
                    this.setState({ wasChanged: false, currentValueRerenderer: Date.now() });
                    return;
                  }
                  if(!this.state.wasChanged && !saveOnTabOut && value == nextValue)return;
                  if (isInteger && !nextValue) {
                    onChange && onChange('0.00', event)
                  }
                  onBlur && onBlur(nextValue, event)
                  this.setState({currentValueRerenderer:Date.now()})
                  this.setState({wasChanged:false});
                  triggerFormValidationSoon()
                }}
                onKeyUp={e =>{
                  onKeyUp && onKeyUp(e)
                }}
                disabled={disabled}
                onMouseDown={e => {
                  e.stopPropagation()
                  penClicked && penClicked(e)
                }}
                onFocus={event => {
                  const normalizedFocusValue = normalizeDisplayValue(value);
                  // Keep in-progress / rubber-bind local value; do not clobber with a stale prop
                  const keepLocalValue =
                    persistOnBlurOnly &&
                    this.state.wasChanged &&
                    this.state.focusValueX != null;
                  if (
                    !keepLocalValue &&
                    this.state.focusInited &&
                    this.state.focusValueX != normalizedFocusValue
                  ) {
                    this.setState({ wasChanged: true });
                  }
                  this.setState({
                    focusValueX: keepLocalValue
                      ? this.state.focusValueX
                      : normalizedFocusValue,
                    focusInited: true,
                    isFocused: true
                  });
                  event.target && event.target.setAttribute("autocomplete", "nope");
                  handleFocus && handleFocus(event)
                  setINPUTID && setINPUTID(fieldDomId)
                }}
              >
                <input
                  maxLength={maxlength}
                  className={className + ' form-control ' + ` subLabelLength${subLabelText?.length} `}
                />
                {['C$','$'].includes(subLabelText) && !!parseFloat(displayValue) && 
                  <div type="text" className={`moneyDisplayerOverlay length${subLabelText.length}`}>{parseFloat(displayValue).toFixed(subLabelToFixed || 2).toLocaleString('us-US', { style: 'currency', currency: 'USD' }).replaceAll("$","")}</div>
              }
              {this.props.extraPunctuation && this.props.extraPunctuation.includes("zipCodeUnitedStatesx") && displayValue?.length > 5 &&
                  <div type="text" className={`moneyDisplayerOverlay length0`} style={{fontSize:'1rem'}}>
                  {displayValue?.substring(0,5)+"-"+displayValue?.substring(5,90)}
                    </div>
              
              }
                {icon}
                {subLabelText && <p className={subLabelClass}>{subLabelText}</p>}
                {postLabel && !disabled && <p className='right-icon'>{postLabel}</p>}
                {clearInput && (
                  <Icon
                    className={(clearTextSpinner?' minispinner ':'')+' delete-field-icon '}
                    name={clearTextSpinner?'spinner':'close'}
                    style={{width:'20px',height:'20px'}}
                    inverted
                    disabled={clearTextSpinner}
                    link
                    id='delete-text'
                    onClick={() => {
                      this.setState({wasChanged:true});
                      !this.props.disabled && this.emptyField(fieldDomId) && this.setState({currentValueRerenderer:Date.now()})
                    }}
                  />
                )}
              </Input>
            )}
        </Form.Field>
      </React.Fragment>
    )
  }
}

export default InputField
